import {
  AutomationFailureResponseArea,
  AutomationFailureResponseReason,
  AutomationFailureResponseStatus,
} from '@urbanx/agx.support.client';
import {
  AgxSelectChip,
  AgxSpacer,
  AgxTextInput,
  Icon_MagnifyingGlass,
  type SelectChipOption,
} from '@urbanx/agx.ui-components2';
import debounce from 'lodash-es/debounce';
import { useEffect, useRef } from 'react';
import { getAutomationAreaLabel, getAutomationReasonLabel } from './automationFailurePresentation';

export type AutomationFailureOccurredPeriod = 'today' | 'last7Days' | 'last30Days' | 'allTime';

export interface AutomationErrorFilterValues {
  search?: string;
  area?: AutomationFailureResponseArea;
  reason?: AutomationFailureResponseReason;
  agencyId?: string;
  status?: AutomationFailureResponseStatus;
  occurredPeriod: AutomationFailureOccurredPeriod;
}

export interface AutomationErrorFiltersProps {
  value: AutomationErrorFilterValues;
  agencyOptions?: SelectChipOption[];
  onChange: (patch: Partial<AutomationErrorFilterValues>) => void;
}

const areaOptions = Object.values(AutomationFailureResponseArea).map((value) => ({
  value,
  label: getAutomationAreaLabel(value),
}));
const reasonOptions = Object.values(AutomationFailureResponseReason).map((value) => ({
  value,
  label: getAutomationReasonLabel(value),
}));
const statusOptions: SelectChipOption[] = [
  { value: AutomationFailureResponseStatus.Unresolved, label: 'Unresolved' },
  { value: AutomationFailureResponseStatus.Resolved, label: 'Resolved' },
];
const occurredOptions: SelectChipOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'last7Days', label: 'Last 7 days' },
  { value: 'last30Days', label: 'Last 30 days' },
  { value: 'allTime', label: 'All time' },
];

const selectedValue = <T extends string>(options: SelectChipOption[]) =>
  options[0]?.value as T | undefined;

export const AutomationErrorFilters = ({
  value,
  agencyOptions,
  onChange,
}: AutomationErrorFiltersProps) => {
  const onChangeRef = useRef(onChange);
  const debouncedSearchChangeRef = useRef<ReturnType<typeof debounce>>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    const debouncedSearchChange = debounce((search: string) => {
      onChangeRef.current({ search: search || undefined });
    }, 300);
    debouncedSearchChangeRef.current = debouncedSearchChange;

    return () => debouncedSearchChange.cancel();
  }, []);

  return (
    <AgxSpacer
      className="automationErrorFilters"
      direction="horizontal"
      contentAlignment="center"
      gap="sm"
      padding="md"
      fill
      style={{
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 3,
        borderRadius: 'var(--radius-6xl)',
        background: 'var(--bg-secondary)',
      }}
    >
      <AgxSpacer style={{ flex: '0 1 360px', width: 'min(360px, 100%)', maxWidth: '100%' }}>
        <AgxTextInput
          id="automation-error-search"
          type="text"
          placeholder="Search by address, reference or automation"
          border="rounded"
          hideOptionalText
          leftIcon={<Icon_MagnifyingGlass color="var(--fg-tertiary)" height={15} width={15} />}
          onChange={(search) => debouncedSearchChangeRef.current?.(search)}
          defaultValue={value.search}
          minWidth="320px"
          noHeader
        />
      </AgxSpacer>

      <AgxSpacer
        direction="horizontal"
        gap="xs"
        contentJustification="end"
        wrap
        style={{ flex: '1 1 auto', minWidth: 0 }}
      >
        <AgxSelectChip
          id="automation-error-area-filter"
          variant="chip"
          label="Area"
          size="medium"
          type="single-select"
          options={areaOptions}
          defaultSelection={value.area}
          onSelect={(options) =>
            onChange({ area: selectedValue<AutomationFailureResponseArea>(options) })
          }
          onReset={() => onChange({ area: undefined })}
          maxLabels={1}
        />

        <AgxSelectChip
          id="automation-error-reason-filter"
          variant="chip"
          label="Reason"
          size="medium"
          type="single-select"
          options={reasonOptions}
          defaultSelection={value.reason}
          onSelect={(options) =>
            onChange({ reason: selectedValue<AutomationFailureResponseReason>(options) })
          }
          onReset={() => onChange({ reason: undefined })}
          maxLabels={1}
        />

        {agencyOptions && (
          <AgxSelectChip
            id="automation-error-agency-filter"
            variant="chip"
            label="Agency"
            size="medium"
            type="single-select"
            options={agencyOptions}
            defaultSelection={value.agencyId}
            onSelect={(options) => onChange({ agencyId: selectedValue<string>(options) })}
            onReset={() => onChange({ agencyId: undefined })}
            searchPlaceHolder="Search agency"
            maxLabels={1}
          />
        )}

        <AgxSelectChip
          id="automation-error-status-filter"
          variant="chip"
          label="Status"
          size="medium"
          type="single-select"
          options={statusOptions}
          defaultSelection={value.status}
          onSelect={(options) =>
            onChange({ status: selectedValue<AutomationFailureResponseStatus>(options) })
          }
          onReset={() => onChange({ status: undefined })}
          maxLabels={1}
        />

        <AgxSelectChip
          id="automation-error-occurred-filter"
          variant="chip"
          label="Occurred"
          size="medium"
          type="single-select"
          options={occurredOptions}
          defaultSelection={value.occurredPeriod}
          onSelect={(options) => {
            const occurredPeriod = selectedValue<AutomationFailureOccurredPeriod>(options);
            if (occurredPeriod) onChange({ occurredPeriod });
          }}
          onReset={() => onChange({ occurredPeriod: 'allTime' })}
          maxLabels={1}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};
